import React from "react";
import { Form } from "@weezevent/nacre";
import { useController } from "react-hook-form";
import PropTypes from "prop-types";
import useReadonlyContext from "../hooks/useReadonlyContext";
import MultiSelectInput from "./components/MultiSelectInput/MultiSelectInput";
import useFormPrefix from "../hooks/useFormPrefix";
import "./FormFieldMutliSelect.scss";

/**
 * This component is an input to manage a multiple select with multiple options.
 * It's a controlled field that deals with react-hook-form. Must be wrapped by a FormContext.
 */
const FormFieldMultiSelect = ({
	name,
	rules,
	defaultValue,
	label,
	search,
	allowAdditions,
	additionLabel,
	options,
	onChange: onChangeAdditional,
	onAddItem,
	disabled,
	placeholder
}) => {
	const prefix = useFormPrefix();
	const controlName = prefix ? `${prefix}.${name}` : name;
	const { isDisabled } = useReadonlyContext(controlName, disabled);
	const required = rules.required && !isDisabled;

	const {
		field: { onChange, value },
		fieldState: { error }
	} = useController({
		name: controlName,
		rules: {
			...rules,
			required
		},
		defaultValue
	});

	const onChangeValue = React.useCallback(
		({ value: v }) => {
			onChange(v);
			onChangeAdditional?.(v);
		},
		[onChange, onChangeAdditional]
	);

	return (
		<Form.Field>
			{!!label && <Form.Label required={required}>{label}</Form.Label>}
			<div
				className={`${
					error ? "wz-c-form-field-multi-select__field--error" : ""
				}`}
			>
				<MultiSelectInput
					placeholder={placeholder}
					values={value}
					onChange={onChangeValue}
					error={!!error}
					search={search}
					allowAdditions={allowAdditions}
					additionLabel={additionLabel}
					options={options}
					onAddItem={onAddItem}
					disabled={isDisabled}
				/>
			</div>
			{error && (
				<span className="wz-c-form-field-multi-select__error-message">
					{error.message}
				</span>
			)}
		</Form.Field>
	);
};

FormFieldMultiSelect.propTypes = {
	/**
	 * The name of the field. This name must be unique in the form.
	 * Used by react-hook-form.
	 */
	name: PropTypes.string.isRequired,
	/**
	 * The default value of the field.
	 * Used by react-hook-form.
	 */
	defaultValue: PropTypes.array,
	/**
	 * The object of rules to apply to validate the field.
	 * Used by react-hook-form.
	 */
	rules: PropTypes.object,
	/**
	 * The label of the field to display.
	 */
	label: PropTypes.node,
	/**
	 * The function to call when the field changes.
	 */
	placeholder: PropTypes.string,
	/**
	 * The placeholder of the field to display
	 */
	onChange: PropTypes.func,
	/**
	 * Whether we allow to search a value in the input among the options.
	 */
	search: PropTypes.bool,
	/**
	 * Whether we allow to search a value in the input among the options.
	 */
	allowAdditions: PropTypes.bool,
	/**
	 * The 'add' label to display for the addition value (if allowAdditions is true).
	 */
	additionLabel: PropTypes.string,
	/**
	 * List of object that stores the options available in the selector.
	 */
	options: PropTypes.arrayOf(PropTypes.object),
	/**
	 * The function to trigger when we add a new value (if allowAdditions true).
	 * The function take the typed value from the input and must return an option object ({ text, value }) or null.
	 */
	onAddItem: PropTypes.func,
	/**
	 * Item is disabled
	 */
	disabled: PropTypes.bool,
	/**
	 * Control
	 */
	control: PropTypes.object
};

FormFieldMultiSelect.defaultProps = {
	defaultValue: [],
	rules: {},
	label: undefined,
	search: true,
	allowAdditions: false,
	onChange: null,
	additionLabel: null,
	options: [],
	onAddItem: null,
	disabled: false,
	control: {},
	placeholder: "Select options..."
};

export default FormFieldMultiSelect;
